// Per-agent semantic cache (ADR-022).
//
// The card is honest about three things the switch alone would hide: where the
// answers are kept (one of the tenant's own vector stores), which capabilities
// make this agent ineligible (and why that is a hard rule, not a preference),
// and whether the cache is actually earning its keep — the hit rate over
// recent lookups.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Zap } from "lucide-react";
import * as React from "react";

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
  selectClass,
} from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { clearSemanticCache, getSemanticCache, updateSemanticCache } from "@/lib/api/endpoints";
import { Link } from "@/components/ui/link";
import { usePathname } from "@/lib/navigation";
import { useEmbeddingModels, useVectorStores } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import { withReturnTo } from "@/lib/return-to";
import type { SemanticCacheSettings } from "@/types/api";
import { useIsOrgAdmin } from "@/lib/access";

const TTL_OPTIONS: { label: string; seconds: number }[] = [
  { label: "1 hour", seconds: 3_600 },
  { label: "6 hours", seconds: 21_600 },
  { label: "24 hours", seconds: 86_400 },
  { label: "7 days", seconds: 604_800 },
  { label: "30 days", seconds: 2_592_000 },
];

export function SemanticCacheCard({ agentSlug }: { agentSlug: string }) {
  const settings = useQuery({
    queryKey: qk.semanticCache(agentSlug),
    queryFn: () => getSemanticCache(agentSlug),
    enabled: !!agentSlug,
  });

  return (
    <Card className="mb-6">
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <Zap className="h-4 w-4 text-primary" />
        <CardTitle className="text-base">Semantic cache</CardTitle>
        {settings.data?.enabled ? (
          <Badge variant="success">On</Badge>
        ) : (
          <Badge variant="secondary">Off</Badge>
        )}
      </CardHeader>
      <CardContent>
        {settings.isLoading ? (
          <Skeleton className="h-28 w-full" />
        ) : settings.isError ? (
          <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
        ) : settings.data ? (
          <CacheForm agentSlug={agentSlug} current={settings.data} />
        ) : null}
      </CardContent>
    </Card>
  );
}

function CacheForm({ agentSlug, current }: { agentSlug: string; current: SemanticCacheSettings }) {
  const qc = useQueryClient();
  const models = useEmbeddingModels();
  const stores = useVectorStores();
  const currentPath = usePathname();
  // Vector stores are added by org admins only.
  const canManage = useIsOrgAdmin();

  const [enabled, setEnabled] = React.useState(current.enabled);
  const [threshold, setThreshold] = React.useState(String(current.threshold));
  const [ttl, setTtl] = React.useState(current.ttl_seconds);
  const [modelSlug, setModelSlug] = React.useState(current.embedding_model_slug ?? "");
  const [storeSlug, setStoreSlug] = React.useState(current.vector_store_slug ?? "");
  const [confirmClear, setConfirmClear] = React.useState(false);

  // Re-seed when the server copy changes (after a save, or a refetch).
  React.useEffect(() => {
    setEnabled(current.enabled);
    setThreshold(String(current.threshold));
    setTtl(current.ttl_seconds);
    setModelSlug(current.embedding_model_slug ?? "");
    setStoreSlug(current.vector_store_slug ?? "");
  }, [current]);

  const save = useMutation({
    mutationFn: () =>
      updateSemanticCache(agentSlug, {
        enabled,
        threshold: Number(threshold),
        ttl_seconds: ttl,
        embedding_model_slug: modelSlug || null,
        vector_store_slug: storeSlug || null,
      }),
    onSuccess: (next) => {
      qc.setQueryData(qk.semanticCache(agentSlug), next);
      toast.success(next.enabled ? "Semantic cache is on." : "Semantic cache saved.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const clear = useMutation({
    mutationFn: () => clearSemanticCache(agentSlug),
    onSuccess: () => {
      toast.success("Cached answers cleared.");
      setConfirmClear(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const thresholdNum = Number(threshold);
  const thresholdValid =
    Number.isFinite(thresholdNum) && thresholdNum >= 0.5 && thresholdNum <= 0.999;
  const blocked = current.blockers.length > 0;
  const { stats } = current;
  const ttlOptions = TTL_OPTIONS.some((o) => o.seconds === ttl)
    ? TTL_OPTIONS
    : [...TTL_OPTIONS, { label: `${Math.round(ttl / 60)} minutes`, seconds: ttl }];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Answers a repeated question — even when it is worded differently — from a previous answer of
        the same agent version, skipping the LLM, knowledge-base search and tools. Only the first
        question of a conversation is cached; follow-ups always reach the model.
      </p>

      {stores.data?.length === 0 ? (
        <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
          Cached answers are kept in one of your vector stores, and none is registered yet —{" "}
          {canManage ? (
            <Link
              href={withReturnTo("/vector-stores", currentPath)}
              className="underline underline-offset-4"
            >
              add one under Vector stores
            </Link>
          ) : (
            "ask an org admin to add one"
          )}
          .
        </div>
      ) : null}

      {blocked ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <div className="font-medium">This agent can&apos;t use the cache.</div>
          <p className="mt-1 text-muted-foreground">
            Every caller shares an agent&apos;s cache, so its answers must not depend on who is
            asking. These capabilities can return caller-specific data:
          </p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {current.blockers.map((b) => (
              <li key={`${b.version}:${b.tool_name}`}>
                <Badge variant="outline" className="font-mono text-[11px]">
                  {b.tool_name} · v{b.version}
                </Badge>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">
            Knowledge-base tools and static-token MCP tools are fine; remove the others (or switch
            MCP tools to static-token auth) to enable caching.
          </p>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input accent-primary"
            checked={enabled}
            disabled={blocked && !current.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Serve repeated questions from the cache
        </label>

        <div className="space-y-1.5">
          <Label htmlFor="sc-store">Vector store</Label>
          <select
            id="sc-store"
            className={selectClass}
            value={storeSlug}
            onChange={(e) => setStoreSlug(e.target.value)}
          >
            <option value="">Choose a vector store…</option>
            {(stores.data ?? []).map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.name} ({s.provider})
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Where cached answers are kept. Changing it starts an empty cache.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sc-model">Embedding model</Label>
          <select
            id="sc-model"
            className={selectClass}
            value={modelSlug}
            onChange={(e) => setModelSlug(e.target.value)}
          >
            <option value="">Choose a model…</option>
            {(models.data ?? []).map((m) => (
              <option key={m.slug} value={m.slug} disabled={m.discarded || !m.in_catalog}>
                {m.name} ({m.model})
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Questions are compared as vectors from this model. Changing it starts an empty cache.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sc-threshold">Similarity threshold</Label>
          <Input
            id="sc-threshold"
            type="number"
            step="0.01"
            min={0.5}
            max={0.999}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            0.92 is a safe default. Lower matches looser rewordings but risks answering a different
            question; the audit log shows each lookup&apos;s score to tune against.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sc-ttl">Keep answers for</Label>
          <select
            id="sc-ttl"
            className={selectClass}
            value={ttl}
            onChange={(e) => setTtl(Number(e.target.value))}
          >
            {ttlOptions.map((o) => (
              <option key={o.seconds} value={o.seconds}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Publishing or editing a version never serves the old version&apos;s answers.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Last 30 days</Label>
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
            {stats.lookups_30d.toLocaleString()} lookups
            {stats.window > 0 ? (
              <span className="text-muted-foreground">
                {" "}
                · {Math.round((stats.hits_in_window / stats.window) * 100)}% hits over the last{" "}
                {stats.window.toLocaleString()}
              </span>
            ) : null}
            {stats.last_hit_at ? (
              <div className="text-xs text-muted-foreground">
                Last hit {new Date(stats.last_hit_at).toLocaleString()}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!current.vector_store_slug || !current.embedding_model_slug || clear.isPending}
          onClick={() => setConfirmClear(true)}
        >
          Clear cached answers
        </Button>
        <Button
          disabled={save.isPending || !thresholdValid || (enabled && (!modelSlug || !storeSlug))}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title="Clear cached answers?"
        description="Every stored answer for this agent is deleted. New questions start filling the cache again right away."
        pending={clear.isPending}
        onConfirm={() => clear.mutate()}
      />
    </div>
  );
}
